import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
} from "@mui/material";
import { Share } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { useConferenceStream } from "./UseConferenceStream";
import StreamViewer from "../../../shared-ui/StreamViewer";
import ShareControls from "../../../shared-ui/ShareControls";
import ConferenceSidePanel from "../../../shared-ui/ConferenceSidePanel";
import ShareDrawer from "../../../shared-ui/ShareDrawer";
import { tokens } from "../../../../../../../../theme";
import apiService from "../../../../../../../../service/Api";
import { toast } from "react-toastify";
import HeaderSection from "./components/HeaderSection";
import AgendaSection from "./components/AgendaSection";
import KeynotePanelistsSection from "./components/SpeakersSection";
import LocationSection from "./components/LocationSection";
import GlobalPartnersSection from "./components/PartnersSection";
import AddSpeakerDialog from "./components/AddSpeakerDialog";
import AddAgendaDialog from "./components/AddAgendaDialog";
import AddPartnerDialog from "./components/AddPartnerDialog";
import { CalendarEvent } from "../../../types/AddtoCalendarutils";

interface ISpeaker {
  id: number;
  name: string;
  image?: string;
  role?: string;
  email?: string;
  phone_number?: string;
}

interface IAgendaItem {
  id: number;
  stream: number;
  agenda?: string;
  title: string;
  start_time: string;
  end_time: string;
  time: string;
  type?: "keynote" | "panel" | "break" | "workshop";
  is_active?: boolean;
  position?: number;
}

interface IPartner {
  id: number;
  stream: number;
  company_name: string;
  logo: string;
  website?: string;
  position?: number;
  is_active?: boolean;
}

interface PartnerFormState {
  company_name: string;
  website: string;
  logo: File | null;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function ConferenceStreamPage() {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);

  const [speakerDialogOpen, setSpeakerDialogOpen] = useState(false);
  const [, setSubmittingSpeaker] = useState(false);
  const [speakers, setSpeakers] = useState<ISpeaker[]>([]);
  const [agendaDialogOpen, setAgendaDialogOpen] = useState(false);
  const [agendaContent, setAgendaContent] = useState<string>("");
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [submittingPartner, setSubmittingPartner] = useState(false);

  const emptyPartnerForm: PartnerFormState = {
    company_name: "",
    website: "",
    logo: null,
  };

  const [partnerForm, setPartnerForm] =
    useState<PartnerFormState>(emptyPartnerForm);

  const {
    streams,
    activeStreamIndex,
    setActiveStreamIndex,
    currentStream,
    deceasedData,
    messages,
    streamProgram,
    isLoadingStreams,
    downloadingProgram,
    isAdmin,
    memorialTheme,
    socketRef,
    handleApprove,
    handleDelete,
    handleDownloadProgram,
  } = useConferenceStream();

  const fetchSpeakers = useCallback(async () => {
    try {
      const res = await apiService.get<{ results: ISpeaker[] } | ISpeaker[]>(
        "/api/individuals/event-speaker/",
      );
      const data = res.data as { results?: ISpeaker[] } | ISpeaker[];
      setSpeakers(Array.isArray(data) ? data : (data.results ?? []));
    } catch (err) {
      console.error("Failed to fetch speakers", err);
    }
  }, []);

  const fetchAgendaItems = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      const res = await apiService.get<
        { results: IAgendaItem[] } | IAgendaItem[]
      >(`/api/streams/stream-program/?stream=${currentStream.id}`);

      const data = res.data as { results?: IAgendaItem[] } | IAgendaItem[];

      const items = Array.isArray(data) ? data : (data.results ?? []);

      // Take the most recently created one instead of the last in array
      const latestItem = items.sort((a, b) => b.id - a.id)[0];

      setAgendaContent(latestItem?.agenda || "");
    } catch (err) {
      console.error("Failed to fetch agenda items", err);
      toast.error("Failed to load agenda.");
    }
  }, [currentStream?.id]);

  const fetchPartners = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      const res = await apiService.get<{ results: IPartner[] } | IPartner[]>(
        `/api/streams/event-partner/?stream=${currentStream.id}`,
      );

      const data = res.data as { results?: IPartner[] } | IPartner[];
      setPartners(Array.isArray(data) ? data : (data.results ?? []));
    } catch (err) {
      console.error("Failed to fetch partners", err);
      toast.error("Failed to load partners.");
    }
  }, [currentStream?.id]);

  useEffect(() => {
    fetchAgendaItems();
  }, [fetchAgendaItems]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    fetchSpeakers();
  }, [fetchSpeakers]);

  const handleAddSpeaker = async (formDataInput: {
    name: string;
    role: string;
    email: string;
    phone_number: string;
    image: File | null;
  }) => {
    if (!formDataInput.name.trim()) {
      toast.error("Speaker name is required.");
      return;
    }

    try {
      setSubmittingSpeaker(true);

      const formData = new FormData();
      formData.append("name", formDataInput.name);

      if (formDataInput.role) formData.append("role", formDataInput.role);
      if (formDataInput.email) formData.append("email", formDataInput.email);
      if (formDataInput.phone_number) {
        formData.append("phone_number", formDataInput.phone_number);
      }
      if (formDataInput.image) {
        formData.append("image", formDataInput.image, formDataInput.image.name);
      }

      await apiService.post("/api/individuals/event-speaker/", formData);

      toast.success("Speaker added successfully!");
      setSpeakerDialogOpen(false);
      await fetchSpeakers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add speaker.");
    } finally {
      setSubmittingSpeaker(false);
    }
  };

  const handleAddPartner = async () => {
    if (!currentStream?.id) {
      toast.error("No stream selected.");
      return;
    }

    if (!partnerForm.company_name.trim()) {
      toast.error("Company name is required.");
      return;
    }

    if (!partnerForm.logo) {
      toast.error("Partner logo is required.");
      return;
    }

    try {
      setSubmittingPartner(true);

      const formData = new FormData();
      formData.append("stream_id", String(currentStream.id));
      formData.append("company_name", partnerForm.company_name);
      formData.append("position", String(partners.length + 1));
      formData.append("is_active", "true");

      if (partnerForm.website) {
        formData.append("website", partnerForm.website);
      }

      if (partnerForm.logo) {
        formData.append("logo", partnerForm.logo, partnerForm.logo.name);
      }

      await apiService.post("/api/streams/event-partner/", formData);

      toast.success("Partner added successfully!");
      setPartnerDialogOpen(false);
      setPartnerForm(emptyPartnerForm);
      await fetchPartners();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add partner.");
    } finally {
      setSubmittingPartner(false);
    }
  };

  const handleAddAgenda = async (html: string) => {
    try {
      const formData = new FormData();
      formData.append("stream_id", String(currentStream.id));
      formData.append("agenda", html);

      await apiService.post("/api/streams/stream-program/", formData);

      toast.success("Program uploaded successfully");
      setAgendaContent(html);
      await fetchAgendaItems();
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload program");
    }
  };

  const handleDeleteSpeaker = async (id: number) => {
    if (!window.confirm("Are you sure you want to remove this speaker?"))
      return;
    try {
      await apiService.delete(`/api/individuals/event-speaker/${id}/`);
      toast.success("Speaker removed successfully!");
      setSpeakers((prev) => prev.filter((sp) => sp.id !== id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove speaker.");
    }
  };

  if (isLoadingStreams) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!streams.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>No streams found for this booking.</Typography>
      </Box>
    );
  }

  const eventTitle =
    currentStream?.title || currentStream?.booking?.service || "Event";

  const eventDate = currentStream?.date
    ? new Date(currentStream.date).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const eventVenue =
    currentStream?.event_details?.venue?.name ||
    currentStream?.booking?.events?.[0]?.venue?.name ||
    "";

  const eventCity = currentStream?.city || "";

  const hasAgendaItems = agendaContent.length > 0;
  const hasSpeakers = speakers.length > 0;
  const hasPartners = partners.length > 0;

  const showAgendaSection = isAdmin || hasAgendaItems;
  const showSpeakersSection = isAdmin || hasSpeakers;
  const showPartnersSection = isAdmin || hasPartners;

  const conferenceCalendarEvent: CalendarEvent | null = (() => {
    const { date, start, end } = currentStream ?? {};
    if (!date || !start || !end) return null;

    const startDate = new Date(`${date}T${start}+02:00`);
    const endDate = new Date(`${date}T${end}+02:00`);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

    // Use full address from event_details.venue if available, otherwise fall back
    const venueDetails = currentStream?.event_details?.venue;
    const location = venueDetails
      ? [
          venueDetails.name,
          venueDetails.address_one,
          venueDetails.address_two,
          venueDetails.city,
        ]
          .filter(Boolean)
          .join(", ")
      : [eventVenue, eventCity].filter(Boolean).join(", ");

    return {
      title: eventTitle,
      location: location || undefined,
      startDate,
      endDate,
    };
  })();
  return (
    <Box sx={{ minHeight: "100vh" }}>
      <HeaderSection
        eventTitle={eventTitle}
        eventDate={eventDate}
        eventVenue={eventVenue}
        eventCity={eventCity}
        targetDate={eventDate}
      />

      <Container
        maxWidth={isMobile ? "sm" : "xl"}
        sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 10 } }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <StreamViewer
                  streams={streams}
                  activeStream={activeStreamIndex}
                  onStreamChange={setActiveStreamIndex}
                  memorialTheme={memorialTheme}
                  currentStream={currentStream}
                  deceasedName={deceasedData?.first_name}
                  hideTabs={false}
                />

                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                  }}
                >
                  {!isMobile ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      flexWrap="wrap"
                      sx={{ width: "100%" }}
                    >
                      <ShareControls
                        streamData={currentStream}
                        theme={memorialTheme}
                        donationsEnabled={
                          currentStream?.go_fund_me_enabled || false
                        }
                        streamProgram={streamProgram}
                        handleDownloadProgram={handleDownloadProgram}
                        downloadingProgram={downloadingProgram}
                        calendarEvent={conferenceCalendarEvent} // ← only here
                      />
                    </Stack>
                  ) : (
                    <Box
                      sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!streamProgram || downloadingProgram}
                        onClick={handleDownloadProgram}
                        sx={{ textTransform: "none", borderRadius: 5 }}
                      >
                        {downloadingProgram ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          "Download Program"
                        )}
                      </Button>

                      <IconButton
                        onClick={() => setShareDrawerOpen(true)}
                        sx={{
                          bgcolor: "info.main",
                          color: "#fff",
                          width: 40,
                          height: 40,
                        }}
                      >
                        <Share />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <ConferenceSidePanel
                streams={streams}
                activeStream={activeStreamIndex}
                onStreamChange={setActiveStreamIndex}
                memorialTheme={memorialTheme}
                messages={messages}
                isAdmin={isAdmin}
                socketRef={socketRef}
                isMobile={isMobile}
                handleApprove={handleApprove}
                handleDelete={handleDelete}
                colors={colors}
              />
            </Grid>
          </Grid>

          <Stack spacing={3}>
            {showAgendaSection && (
              <motion.div variants={itemVariants}>
                <AgendaSection
                  agendaHtml={agendaContent}
                  isAdmin={isAdmin}
                  onAddItem={() => setAgendaDialogOpen(true)}
                />
              </motion.div>
            )}

            {showSpeakersSection && (
              <motion.div variants={itemVariants}>
                <KeynotePanelistsSection
                  speakers={speakers}
                  isAdmin={isAdmin}
                  onAddPanelist={() => setSpeakerDialogOpen(true)}
                  onDeletePanelist={handleDeleteSpeaker}
                />
              </motion.div>
            )}

            <motion.div variants={itemVariants}>
              <LocationSection
                eventVenue={eventVenue}
                eventCity={eventCity}
                googleMapsApiKey={
                  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""
                }
              />
            </motion.div>

            {showPartnersSection && (
              <motion.div variants={itemVariants}>
                <GlobalPartnersSection
                  partners={partners}
                  isAdmin={isAdmin}
                  onAddPartner={() => setPartnerDialogOpen(true)}
                />
              </motion.div>
            )}
          </Stack>
        </motion.div>
      </Container>

      <ShareDrawer
        open={shareDrawerOpen}
        onClose={() => setShareDrawerOpen(false)}
        streamData={currentStream}
      />
      <AddSpeakerDialog
        open={speakerDialogOpen}
        onClose={() => setSpeakerDialogOpen(false)}
        onSubmit={handleAddSpeaker}
      />
      <AddAgendaDialog
        open={agendaDialogOpen}
        onClose={() => setAgendaDialogOpen(false)}
        onSubmit={handleAddAgenda}
        initialContent={agendaContent}
      />
      <AddPartnerDialog
        open={partnerDialogOpen}
        onClose={() => setPartnerDialogOpen(false)}
        onSubmit={handleAddPartner}
        form={partnerForm}
        setForm={setPartnerForm}
        submitting={submittingPartner}
      />
    </Box>
  );
}
